// Adaptive resolution for the Shimmering Prism background.
//
// Ported from the standalone demo (src/newbackground/js/quality.js). Scales the
// renderer pixel ratio within [min, max] to hold a ~60fps frame-time target, using
// a frame-time EMA + hysteresis (separate up/down frame counts) to avoid visible
// resolution "pumping". apply() also keeps the shader `resolution` uniform in sync.
//
// `limits` is optional and backward-compatible: omitted → [0.5, 1.0] (the original
// hardcoded range). The mobile background passes a lower, capped range so phones
// render at a reduced resolution (see ShimmeringBackground / ShimmeringPrism).

export function createAdaptiveResolution(renderer, resolutionUniformValue, initial = 0.75, limits = {}) {

    const min = typeof limits.min === 'number' ? limits.min : 0.5;
    const max = typeof limits.max === 'number' ? limits.max : 1.0;
    let quality = Math.min(max, Math.max(min, initial)); // clamp the requested initial into [min, max]
    let frameTimeEMA = 1000.0 / 60.0;
    let resLowFrames = 0;
    let resHighFrames = 0;

    function apply(q) {
        quality = q;
        renderer.setPixelRatio(q);
        renderer.setSize(window.innerWidth, window.innerHeight);
        resolutionUniformValue.set(window.innerWidth * q, window.innerHeight * q);
    }

    function tick(dtMs) {
        // Ignore hitch / tab-restore frames so they don't trigger a spurious downscale.
        if (dtMs > 0.0 && dtMs < 100.0) {
            frameTimeEMA = frameTimeEMA * 0.9 + dtMs * 0.1;
            if (frameTimeEMA > 18.0) {
                resHighFrames = 0;
                if (++resLowFrames >= 30 && quality > min) {
                    apply(Math.max(min, Math.round((quality - 0.1) * 100) / 100));
                    resLowFrames = 0;
                }
            } else if (frameTimeEMA < 14.0) {
                resLowFrames = 0;
                if (++resHighFrames >= 60 && quality < max) {
                    apply(Math.min(max, Math.round((quality + 0.1) * 100) / 100));
                    resHighFrames = 0;
                }
            } else {
                resLowFrames = 0;
                resHighFrames = 0;
            }
        }
    }

    function onResize() {
        apply(quality);
    }

    apply(quality); // initial (clamped) pixel ratio + size + resolution uniform

    return {
        tick,
        onResize,
        apply,
        get current() { return quality; },
    };
}
